import { Box } from '@mui/material'
import AdxPopper from 'components/reusable/AdxPopper'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from 'react-query'
import {
  downloadBankTextFile,
  downloadBankCsvFile,
  downloadMonthlyReport,
  downloadSalaryReportPdf,
  downloadPerkesoSocsoEisTextFile,
  reopenPayroll,
} from 'services/api/modules/payroll/payroll-history'
import Bugsnag from '@bugsnag/js'
import ErrorRunPayrollModal from '../../payroll-dashboard/components/ErrorRunPayrollModal'
import AdxButton from 'components/custom/AdxButton'
import SnackbarRecruitment from 'module/recruitment/reusable/SnackbarRecruitment'
import { downloadResponseFile } from 'services/helper/fileDownload'

const HistoryHeader = ({ payrollData, time_id }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [promptErrors, setPromptErrors] = useState(false)
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false)
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState('')
  const navigate = useNavigate()
  const { mutate: handleReopenPayroll } = useMutation(() => reopenPayroll(time_id), {
    onSuccess: () => {
      setIsLoading(false)
      navigate(
        `/app/payroll/regular-payroll/${payrollData?.month}-${parseInt(payrollData?.year)}`,
        { state: { item: time_id } }
      )
    },
    onError: (error) => {
      setErrorMessage(error.data.message)
      setPromptErrors(true)
      setIsLoading(false)
    },
  })
  const amendPayroll = () => {
    setIsLoading(true)
    handleReopenPayroll()
  }
  const canAmend = payrollData?.payroll_status !== 0

  //download monthly report
  const { refetch: refetchMonthlyReport } = useQuery(
    ['retrieveMonthlyReport', { time_id }],
    () => downloadMonthlyReport(time_id),
    {
      enabled: false,
      onSuccess: (res) => {
        const reportFilename = `monthly_report_${payrollData?.month}${payrollData?.year}`
        downloadResponseFile(res, {
          fileName: `${reportFilename}.xlsx`,
          contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          successMessage: 'Monthly payroll report downloaded.',
        })
      },
      onError: (error) => {
        if (error.status === 422) {
          console.log(error)
        } else {
          Bugsnag.notify(error.data)
        }
      },
    }
  )
  const downloadReport = () => {
    refetchMonthlyReport()
  }

  //download salary report PDF
  const { refetch: refetchSalaryReportPdf } = useQuery(
    ['salaryReportPdf', { time_id }],
    () => downloadSalaryReportPdf(time_id),
    {
      enabled: false,
      onSuccess: (res) => {
        const monthMap = {
          January: '01',
          February: '02',
          March: '03',
          April: '04',
          May: '05',
          June: '06',
          July: '07',
          August: '08',
          September: '09',
          October: '10',
          November: '11',
          December: '12',
        }
        const month = monthMap[payrollData?.month] || `${payrollData?.month}`.padStart(2, '0')
        const reportFilename = `Salary_Report_${month}_${payrollData?.year}`
        downloadResponseFile(res, {
          fileName: `${reportFilename}.pdf`,
          contentType: 'application/pdf',
          successMessage: 'Salary report PDF downloaded.',
        })
      },
      onError: (error) => {
        if (error.status === 422) {
          console.log(error)
        } else {
          Bugsnag.notify(error.data)
        }
      },
    }
  )
  const downloadSalaryReport = () => {
    refetchSalaryReportPdf()
  }

  const { refetch: refetchPerkesoSocsoEisTextFile } = useQuery(
    ['perkesoSocsoEisTextFile', time_id],
    () => downloadPerkesoSocsoEisTextFile(time_id),
    {
      enabled: false,
      onSuccess: (res) => {
        const reportFilename = `perkeso_socso_eis_${payrollData?.month}${payrollData?.year}`
        downloadResponseFile(res, {
          fileName: `${reportFilename}.txt`,
          contentType: 'text/plain',
          successMessage: 'PERKESO SOCSO + EIS text file downloaded.',
        })
      },
      onError: (error) => {
        if (error.status === 422) {
          console.log(error)
        } else {
          Bugsnag.notify(error.data)
        }
      },
    }
  )
  const downloadPerkesoSocsoEis = () => {
    refetchPerkesoSocsoEisTextFile()
  }
  //download bank text file
  const { refetch: refetchBankTextFile } = useQuery(
    ['bankTextFile', time_id],
    () => downloadBankTextFile(time_id),
    {
      enabled: false,
      onSuccess: (res) => {
        downloadResponseFile(res, {
          fileName: 'salary_maybank.txt',
          contentType: 'text/plain',
          successMessage: 'Salary Maybank text file downloaded.',
        })
      },
      onError: (error) => {
        // Handle validation errors with detailed messages
        if (error.response?.status === 404 || error.response?.status === 422) {
          const errorData = error.response?.data
          if (errorData?.data && Array.isArray(errorData.data)) {
            // Format error messages with staff names
            const errorMessages = errorData.data.join('\n')
            const fullMessage = `${errorData.message || 'Error generating bank text file'}\n\n${errorMessages}`
            setErrorSnackbarMessage(fullMessage)
            setErrorSnackbarOpen(true)
            // Also show detailed error in console for debugging
            console.error('Bank text file generation errors:', errorData.data)
          } else {
            setErrorSnackbarMessage(errorData?.message || 'Error generating bank text file')
            setErrorSnackbarOpen(true)
          }
        } else {
          Bugsnag.notify(error.data)
          setErrorSnackbarMessage('An unexpected error occurred. Please try again.')
          setErrorSnackbarOpen(true)
        }
      },
    }
  )
  const downloadBankFile = () => {
    refetchBankTextFile()
  }

  //download bank CSV file
  const { refetch: refetchBankCsvFile } = useQuery(
    ['bankCsvFile', time_id],
    () => downloadBankCsvFile(time_id),
    {
      enabled: false,
      onSuccess: (res) => {
        downloadResponseFile(res, {
          fileName: 'bank_information.csv',
          contentType: 'text/csv',
          successMessage: 'Bank information CSV downloaded.',
        })
      },
      onError: (error) => {
        if (error.status === 422) {
          console.log(error)
        } else {
          Bugsnag.notify(error.data)
        }
      },
    }
  )
  const downloadBankCsv = () => {
    refetchBankCsvFile()
  }

  return (
    <Box display="flex" justifyContent="end" alignItems="center">
      {/* <Box display="flex" alignItems="flex-start">
        <Icon onClick={handleBack} sx={{ mr: 2, color: 'alternate.main', cursor: 'pointer' }}>
          arrow_back
        </Icon>
        <Box display="flex" flexDirection="column" justifyContent="space-around">
          {loadingData ? (
            <Skeleton width={100} />
          ) : (
            <Typography variant="h4">{`${payrollData?.month} ${payrollData?.year}`}</Typography>
          )}
          {loadingData ? (
            <Skeleton width={300} />
          ) : (
            <Typography
              variant="body2"
              color="light-gray-text.main"
            >{`Payroll > ${payrollData?.month} ${payrollData?.year}`}</Typography>
          )}
        </Box>
      </Box> */}
      <Box display="flex" alignItems="center">
        {canAmend && (
          <AdxButton
            variant={'outlined'}
            isLoading={isLoading}
            disabled={isLoading}
            onClick={amendPayroll}
            sx={{ mx: 2 }}
          >
            Amend
          </AdxButton>
        )}
        <AdxPopper placement="bottom-end">
          <Box onClick={downloadReport}>Export monthly report (.xlsx)</Box>
          <Box onClick={downloadSalaryReport}>Export salary report (.pdf)</Box>
          <Box onClick={downloadPerkesoSocsoEis}>Export PERKESO SOCSO + EIS (.txt)</Box>
          <Box onClick={downloadBankCsv}>Export all bank information (.CSV)</Box>
          <Box onClick={downloadBankFile}>Download Salary Maybank text file (.txt)</Box>
        </AdxPopper>
      </Box>
      <ErrorRunPayrollModal
        isModalOpen={promptErrors}
        errorMessage={errorMessage}
        handleClose={() => setPromptErrors(false)}
      />
      {errorSnackbarOpen && (
        <SnackbarRecruitment
          autoHideDuration={10000}
          status="error"
          TransitionComponent="slide"
          message={errorSnackbarMessage}
          open={errorSnackbarOpen}
          handleClose={() => setErrorSnackbarOpen(false)}
        />
      )}
    </Box>
  )
}

export default HistoryHeader
